/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import Button from '@mui/material/Button';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { MessageType, PageType, PageTypeEnum } from '../algorithm/types';
import { analyticsTrack } from '../analytics_utils';
import { selectOrgView, selectPearlUserInfo, selectUserFlags } from '../Login/userSlice';
import { canEditDocumentVersion, useNewFeedbackUserFlag } from '../permissions';
import { copyToClipboard } from '../utils';
import {
  resetSearchQueryMetaData,
  selectCreatorMode,
  selectDocumentVersionById,
  selectIsAnyMessageFlipped,
  selectTabPageSelected,
} from './chatSlice';
import { InnerMessage } from './components/InnerMessage/InnerMessage';
import { InnerMessageExtra } from './components/InnerMessage/InnerMessageExtra';
import { TabPageContainer } from './components/InnerMessage/TabPageContainer';
import { MessageTooltipDisplayer, TooltipType } from './components/MessageTooltipDisplayer';
import { selectPatientSummaryState } from './components/PatientSummary/patientSummarySlice';
import { ReturnToHereButtonList } from './components/ReturnToHereButton';
import { toggleFeedback } from './DocumentVersionFeedback/feedbackSlice';
import { isDocumentVersionInternalOnly } from '../documentVersionFlags/documentVersionFlags';
import { FhirScopeContext, MessageProvider, usePageIfExists } from './hooks/MessageContext';
import styles from './Message.module.scss';
import { DocumentVersionFavoriteButton } from './DocumentVersionFeedback/DocumentVersionFavoriteButton';
import { DigitalAssistantFeedbackTypes } from './DocumentVersionFeedback/FeedbackType';

export type MessageProps = {
  commentsChild?: React.ReactElement;
  isFlipped?: boolean;
  message: MessageType;
  onKeyToggled: (page: PageType, key: string, value: string) => void;
  compact?: boolean;
  allowPageDelete: boolean;
  allowPageChangeRoot: boolean;
  isLastMessage: boolean /* For styling the last message differently */;
  inAlgorithmView: boolean /* Whether we are in the algorithm creator view, with the graph etc */;
};

/**
 *  This is the non-editing version of the Message.
 *  There is an editable version called MessageFlipped.
 *  In creator flows, editing is opened separately in MessageEditDialog.
 */
export function Message({
  commentsChild,
  message,
  onKeyToggled,
  compact,
  inAlgorithmView,
  isLastMessage,
}: MessageProps) {
  const [tooltip, setTooltip] = React.useState(undefined as TooltipType | undefined);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const orgView = useAppSelector(selectOrgView);
  const flags = useAppSelector(selectUserFlags);
  const document_version_id = message.pages?.[0]?.document_version_id;
  const isAnyMessageFlipped = useAppSelector(selectIsAnyMessageFlipped);
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, document_version_id));
  const canEdit = canEditDocumentVersion(pearlUserInfo, documentVersion);
  const { showContextualization, showTopic, showSummary } = useAppSelector(selectPatientSummaryState);
  const searchMetadata = useAppSelector((state) => state.chat.searchMetadata);
  const isCreatorMode = useAppSelector(selectCreatorMode);

  const dispatch = useAppDispatch();
  // Do not allow editing multiple pages at a time
  const allowEditing = Boolean(
    !isAnyMessageFlipped && isCreatorMode && !message.no_bubble && (message?.pages?.[0]?.id || canEdit)
  );
  const divRef = useRef<HTMLDivElement>(null);
  const viewFeedbackToolbar = useNewFeedbackUserFlag();
  const currentTab = useAppSelector((state) =>
    selectTabPageSelected(state, document_version_id, message?.pages?.[0]?.id)
  );
  const page = message.pages?.[0];

  useEffect(() => {
    if (documentVersion && !message.is_sent && (page?.pageType === PageTypeEnum.GUIDE || page?.isRoot !== false)) {
      console.log('analyticsTrack document_open', documentVersion?.title);
      /* Before making changes to this, inform:
        Cynthia.Walsh@providence.org
        Prateek.Fulsundar@providence.org

        If you modify this, please update the documentation at:
        https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/158662658/Mixpanel+and+Pendo

        The document_open event is sent when the user opens a document version
        by making a search with only one result, or clicking on search result,
        clicking a link or button in an algorithm, etc.

        documentName: The title of the document version
        documentId: The id of the document
        documentVersionId: The id of the document version
        slug: The unique slug of the document (more reliable than title since title can be changed)
        persona: The personas of the document version - e.g. "Provider"
        careCategory: The care categories of the document version - e.g. "Inpatient"
        patientGroup: The patient groups of the document version - e.g. "Adult and Peds"
        documentType: The type of the document - either "DOCUMENT" or "TRIAGE"
        pageType: either 'guide', 'swimlane', or 'multiple_questions'
        session_id: The session id of the user
       */
      analyticsTrack(
        'document_open',
        {
          documentName: documentVersion?.title,
          documentId: documentVersion?.document?.id,
          documentVersionId: documentVersion?.id,
          slug: documentVersion?.document?.slug,
          persona: documentVersion.audiences.personas.join(','),
          careCategory: documentVersion.audiences.care_categories.join(','),
          patientGroup: documentVersion.audiences.patient_groups.join(','),
          documentType: documentVersion?.document?.docType,
          pageType: page?.pageType,
        },
        pearlUserInfo,
        {
          eventName: 'Opened Document After Search',
          index: searchMetadata?.index || 'doc',
          queryID: searchMetadata?.queryID || '',
          objectIDs: searchMetadata?.objectIDs || [],
          positions: searchMetadata?.positions || [],
        }
      );
      dispatch(resetSearchQueryMetaData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, documentVersion?.id]);

  const onShowTooltip = useCallback(
    (element?: HTMLElement, text?: string, header?: string) => {
      setTooltip(
        !text || (text === tooltip?.text && header === tooltip?.header) ? undefined : { element, text, header }
      );
    },
    [tooltip?.header, tooltip?.text]
  );

  useEffect(() => setTooltip(undefined), [isAnyMessageFlipped]);
  useEffect(() => {
    onShowTooltip(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const pageType = message.is_sent ? styles.sent : styles.response;
  const no_bubble = message.no_bubble;
  const hide_message = !inAlgorithmView && message.is_sent && message.hide_sent;

  const className = clsx(styles.message, pageType, compact && styles.compact, no_bubble && styles.no_bubble);
  const messageRootRef = useRef<HTMLDivElement>(null);
  const fhirScopeIdRef = useRef(`fhir-scope-${message.index}`);
  if (hide_message) return null;

  const copyMessageToClipboard = (div: HTMLDivElement | null) => {
    if (div) {
      copyToClipboard(div);
      analyticsTrack(
        'copy_to_clipboard',
        {
          documentName: documentVersion?.title,
          documentId: documentVersion?.document?.id,
          documentVersionId: documentVersion?.id,
          slug: documentVersion?.document?.slug,
          persona: documentVersion?.audiences.personas.join(','),
          careCategory: documentVersion?.audiences.care_categories.join(','),
          patientGroup: documentVersion?.audiences.patient_groups.join(','),
          documentType: documentVersion?.document?.docType,
          pageType: page?.pageType,
          // For message of type 'TABS' , fetch the info from the tabPageSelected selector
          pageId: page?.pageType !== PageTypeEnum.TABS ? page?.id : currentTab?.id,
          pageName: page?.pageType !== PageTypeEnum.TABS ? page?.name : currentTab?.name,
        },
        pearlUserInfo
      );
    }
  };
  function handleFeedbackClick(): void {
    dispatch(toggleFeedback(DigitalAssistantFeedbackTypes.currentTopic));
  }

  // Response from server (or not found message)
  return (
    <MessageProvider message={message}>
      <FhirScopeContext.Provider value={{ scopeId: fhirScopeIdRef.current, scopeRef: messageRootRef }}>
        <div
          id="three-pane-view"
          className={className}
          data-message-index={message.index} /* just for e2e testing */
          data-fhir-scope={fhirScopeIdRef.current}
          data-is-last-message={isLastMessage}
          data-response
          data-user-role={pearlUserInfo?.orgRole}
          data-user-org={pearlUserInfo?.orgCode}
          data-org-view={orgView}
          data-document-version-id={documentVersion?.id}
          data-document-state={documentVersion?.state}
          data-internal-only={isDocumentVersionInternalOnly(documentVersion)}
          // for e2e testing
          data-document-persona={documentVersion?.audiences?.personas}
          data-document-carecategory={documentVersion?.audiences?.care_categories}
          data-document-patientgroup={documentVersion?.audiences?.patient_groups}
          ref={messageRootRef}
        >
          {commentsChild}
          <MessageTooltipDisplayer
            tooltip={tooltip}
            pageId={message.pages?.[0]?.id}
            documentId={documentVersion?.document?.id}
            documentSlug={documentVersion?.document?.slug}
            documentVersionId={message.pages?.[0]?.document_version_id}
            compact={!!compact}
            onClose={() => onShowTooltip(undefined)}
          />
          {/* </div> */}
          {/*For the guide or algorithm,show the feedback container only when the can_see_feedback flag is enabled and the user is not in creatormode.*/}
          <div
            className={clsx(`${styles.content} ${styles['pageType-' + message.pages?.[0]?.pageType]}`, {
              [styles.maxWidthFull as string]: showTopic && !showContextualization && !showSummary,
            })}
            id="topicContent"
            style={{
              visibility: showTopic ? 'visible' : 'hidden',
              display: !showTopic ? 'none' : '',
              paddingTop: !showTopic && showContextualization && showSummary ? '0' : '',
            }}
            ref={divRef}
          >
            <MessageContainer>
              <div
                className={styles.messageActionsContainer}
                style={{
                  paddingTop: page?.pageType === PageTypeEnum.TABS ? '10px' : 0,
                }}
              >
                {flags?.patient_notes_summary && message.pages?.[0] && documentVersion && !isCreatorMode && (
                  <Button id="copy-button" variant="copy" onClick={() => copyMessageToClipboard(divRef.current)}>
                    Copy
                  </Button>
                )}
                {message.pages?.[0] && documentVersion && viewFeedbackToolbar ? (
                  <>
                    <DocumentVersionFavoriteButton documentVersion={documentVersion} />
                    <Button
                      variant="copy"
                      id="documentversion-feedback"
                      className={styles.documentFeedbackBtn}
                      onClick={handleFeedbackClick}
                    >
                      Feedback
                    </Button>{' '}
                  </>
                ) : undefined}
              </div>
              <InnerMessage
                overrideContent={message.content}
                onKeyToggled={onKeyToggled}
                onShowTooltip={onShowTooltip}
                allowEditing={allowEditing}
              />
              {message.extra && <InnerMessageExtra {...message.extra} />}
            </MessageContainer>
            {isLastMessage && !message.is_sent && <ReturnToHereButtonList lastMessageIndex={message.index} />}
          </div>
        </div>
      </FhirScopeContext.Provider>
    </MessageProvider>
  );
}

/** This is a simple wrapper that adds the tabs if needed, adding the toolbar as part of the tabs if needed, so that they can
 *  be stickied together.
 */
export function MessageContainer({ children, toolbar }: { children: React.ReactNode; toolbar?: React.ReactNode }) {
  const page = usePageIfExists();
  if (page?.page && page.page.pageType === PageTypeEnum.TABS) {
    return (
      <TabPageContainer page={page.page} toolbar={toolbar}>
        {children}
      </TabPageContainer>
    );
  } else {
    return (
      <>
        {toolbar && <div className={styles.outerFeedbackContainer}>{toolbar}</div>}
        {children}
      </>
    );
  }
}
